import { useState, useEffect } from "react";
import { apiFetch, can, getPermissions } from "../api";
import { LinkedContactsList, LinkedClaimsList } from "./LinkedLists";
import EditContactModal from "./EditContactModal";  // mutual import: only used in render (no top-level ref) so the cycle resolves fine
import EntityDocuments from "./EntityDocuments";  // DC-3: company document lens (W9, payee docs, correspondence)

// ============================================================================
// EditCompanyModal — shared between the Companies page and the Admin Review
// Queue. Handles PATCH /companies/{id} plus the admin-only
// POST /companies/{id}/approve action, with per-field amber highlighting
// when the current values differ from last_reviewed_snapshot.
//
// Props:
//   company   — company row (must include last_reviewed_snapshot, reviewed_at)
//   schedules — array of { id, name, locks_rate }
//   isAdmin   — whether to show the Approve action and is_active toggle
//   onClose() — close without reloading
//   onSaved() — close and caller should reload their list
// ============================================================================

// Fields tracked in the review snapshot. Must stay in sync with
// _refresh_company_review in main.py.
const REVIEWABLE_FIELDS = ["company_name","city","state","phone","email","notes","default_rate_schedule_id"];

function fieldDiffers(snapshot, row, field) {
  if (!snapshot) return false;
  const s = snapshot[field]; const c = row[field];
  const norm = v => (v === null || v === undefined || v === "") ? null : v;
  return norm(s) !== norm(c);
}

function parseErr(e, fallback) {
  try { return JSON.parse(e.message).detail || fallback; } catch { return fallback; }
}

function Modal({ title, onClose, children, footer }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3>{title}</h3><button className="modal-close" onClick={onClose}>×</button></div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

export default function EditCompanyModal({ company, schedules, isAdmin, onClose, onSaved, onNavigateToClaim, onNavigateToContact }) {
  const [form, setForm] = useState({
    company_name: company.company_name || "",
    city: company.city || "",
    state: company.state || "",
    phone: company.phone || "",
    email: company.email || "",
    notes: company.notes || "",
    default_rate_schedule_id: company.default_rate_schedule_id || "",
    is_active: company.is_active,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  // Pop-out: open a linked contact's modal in place (no page navigation).
  const [openContact, setOpenContact] = useState(null);
  const openLinkedContact = async (id) => {
    try { setOpenContact(await apiFetch(`/contacts/${id}`)); }
    catch (e) { setErr(parseErr(e, "Failed to open contact")); }
  };

  // CONTACT-CREATE-1: "+ Add contact" on the Linked Contacts section — opens
  // the shared create modal locked to this company, as a sibling overlay on
  // top of this modal (NOT the swap pattern: the company edit state survives).
  const [addingContact, setAddingContact] = useState(false);
  const [contactsRefresh, setContactsRefresh] = useState(0);

  // Bordereau-client toggle (BDX) — loaded/saved via the dedicated endpoint,
  // admin-gated (manage_bordereau_profiles).
  const canBdx = can("manage_bordereau_profiles");
  const [bdxEligible, setBdxEligible] = useState(false);
  const [bdxOrig, setBdxOrig] = useState(null);
  useEffect(() => {
    if (!canBdx) return;
    apiFetch(`/companies/${company.id}/bordereau-eligible`)
      .then(d => { const v = !!d.bordereau_eligible; setBdxEligible(v); setBdxOrig(v); })
      .catch(() => {});
  }, []);

  // Build a field-pending map off the loaded row (not the in-progress form),
  // so the amber badge reflects "what's awaiting admin review right now" and
  // doesn't flicker while the user types.
  const snapshot = company.last_reviewed_snapshot;
  const neverReviewed = company.needs_admin_review && !snapshot;
  const isPendingField = (field) => company.needs_admin_review && !neverReviewed && fieldDiffers(snapshot, company, field);
  const pendingStyle = { borderColor: "#f59e0b", background: "#fffbeb" };

  const save = async () => {
    setErr(null);
    if (!form.company_name.trim()) { setErr("Name is required"); return; }
    setSaving(true);
    try {
      const patch = {};
      if (form.company_name.trim() !== (company.company_name || "")) patch.company_name = form.company_name.trim();
      if ((form.city || "") !== (company.city || "")) patch.city = form.city;
      if ((form.state || "") !== (company.state || "")) patch.state = form.state;
      if ((form.phone || "") !== (company.phone || "")) patch.phone = form.phone;
      if ((form.email || "") !== (company.email || "")) patch.email = form.email;
      if ((form.notes || "") !== (company.notes || "")) patch.notes = form.notes;
      const newSched = form.default_rate_schedule_id === "" ? null : Number(form.default_rate_schedule_id);
      const oldSched = company.default_rate_schedule_id ?? null;
      if (newSched !== oldSched) patch.default_rate_schedule_id = newSched;
      if (form.is_active !== company.is_active) patch.is_active = form.is_active;
      const bdxChanged = canBdx && bdxOrig !== null && bdxEligible !== bdxOrig;
      if (bdxChanged) {
        await apiFetch(`/companies/${company.id}/bordereau-eligible`, { method:"PATCH", body: { eligible: bdxEligible } });
      }
      if (Object.keys(patch).length === 0) { (bdxChanged ? onSaved : onClose)(); return; }
      await apiFetch(`/companies/${company.id}`, { method:"PATCH", body: patch });
      onSaved();
    } catch (e) { setErr(parseErr(e, "Failed to save")); setSaving(false); }
  };

  // LD-REV-006 / H18 — the missing button.  `last_reviewed_snapshot` already
  // held the last approved value and the UI only tinted the field amber; this
  // restores it.  Per-field (the tint is per-field), gated on the same
  // capability as Approve — reverting is the reject half of reviewing.
  const revertField = async (field) => {
    const prior = snapshot ? snapshot[field] : null;
    const shown = (prior === null || prior === undefined || prior === "") ? "— empty —" : String(prior);
    if (!window.confirm(`Revert ${field.replace(/_/g, " ")} to the last approved value?\n\n${shown}`)) return;
    setErr(null); setSaving(true);
    try {
      await apiFetch(`/companies/${company.id}/revert-to-snapshot`, { method:"POST", body:{ fields:[field] } });
      onSaved();
    } catch (e) { setErr(parseErr(e, "Failed to revert")); setSaving(false); }
  };
  // Plain function, not a nested component — keeps CRA's lint quiet and the
  // element identity stable.
  // LD-REV-043: the proposer may withdraw (revert) their own unapproved proposal.
  const mineField = (field) => ((company.pending_proposer_by_field || {})[field] === getPermissions().user_id);
  const rev = (field) => ((isAdmin || mineField(field)) && isPendingField(field)) ? (
    <button type="button" className="btn btn-outline btn-sm" onClick={() => revertField(field)}
            disabled={saving} title="Restore the last approved value"
            style={{marginLeft:8, padding:"0 6px", fontSize:10, fontWeight:500, textTransform:"none", letterSpacing:"normal"}}>
      Revert
    </button>
  ) : null;

  const approve = async () => {
    if (!window.confirm(`Approve "${company.company_name}" as reviewed? This will snapshot the current values as the new baseline.`)) return;
    setSaving(true);
    try {
      await apiFetch(`/companies/${company.id}/approve`, { method:"POST" });
      onSaved();
    } catch (e) { setErr(parseErr(e, "Failed to approve")); setSaving(false); }
  };

  // Pop-out: a linked contact was clicked — show its modal in place; closing
  // it returns here.
  if (openContact) {
    // Swap (not stack): clicking through replaces this modal with the contact's.
    // Close/Save closes the whole thing — no back-stepping through a stack.
    return <EditContactModal
      contact={openContact}
      isAdmin={can("approve_contacts")}
      onClose={onClose}
      onSaved={onClose}
      onNavigateToClaim={onNavigateToClaim}
    />;
  }

  return (
    <Modal
      title={`Edit Company: ${company.company_name}`}
      onClose={onClose}
      footer={<>
        {isAdmin && company.needs_admin_review && (
          <button className="btn btn-primary btn-sm" onClick={approve} disabled={saving} style={{marginRight:"auto"}}>Approve as Reviewed</button>
        )}
        <button className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving?"Saving...":"Save"}</button>
      </>}
    >
      {err && <div className="alert-error">{err}</div>}

      {neverReviewed && (
        <div style={{background:"#fffbeb",border:"1px solid #fcd34d",borderRadius:6,padding:"10px 12px",marginBottom:12,fontSize:12,color:"#92400e"}}>
          <strong>Awaiting first admin review.</strong> This company was created by a non-admin and hasn't been approved yet. {isAdmin ? 'You can approve it with "Approve as Reviewed" below once the details look right.' : "An admin will review the details shortly."}
        </div>
      )}
      {company.needs_admin_review && !neverReviewed && (
        <div style={{background:"#fffbeb",border:"1px solid #fcd34d",borderRadius:6,padding:"10px 12px",marginBottom:12,fontSize:12,color:"#92400e"}}>
          <strong>Pending admin review.</strong> Fields highlighted in amber differ from the last approved values. {isAdmin && 'Use "Approve as Reviewed" to accept the current values as the new baseline, or "Revert" beside a field to restore the approved value.'}
        </div>
      )}

      <div className="form-grid">
        <div className="form-group full">
          <label>Company Name *{rev("company_name")}</label>
          <input value={form.company_name} onChange={e=>setForm({...form,company_name:e.target.value})} style={isPendingField("company_name") ? pendingStyle : undefined}/>
        </div>
        <div className="form-group">
          <label>City{rev("city")}</label>
          <input value={form.city} onChange={e=>setForm({...form,city:e.target.value})} style={isPendingField("city") ? pendingStyle : undefined}/>
        </div>
        <div className="form-group">
          <label>State{rev("state")}</label>
          <input value={form.state} onChange={e=>setForm({...form,state:e.target.value})} style={isPendingField("state") ? pendingStyle : undefined}/>
        </div>
        <div className="form-group">
          <label>Phone{rev("phone")}</label>
          <input value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} style={isPendingField("phone") ? pendingStyle : undefined}/>
        </div>
        <div className="form-group">
          <label>Email{rev("email")}</label>
          <input type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} style={isPendingField("email") ? pendingStyle : undefined}/>
        </div>
        <div className="form-group full">
          <label>Default Rate Schedule{rev("default_rate_schedule_id")}</label>
          <select value={form.default_rate_schedule_id} onChange={e=>setForm({...form,default_rate_schedule_id:e.target.value})} style={isPendingField("default_rate_schedule_id") ? pendingStyle : undefined}>
            <option value="">— None —</option>
            {schedules.map(s => <option key={s.id} value={s.id}>{s.name}{s.locks_rate ? " (locked)" : ""}</option>)}
          </select>
        </div>
        <div className="form-group full">
          <label>Notes{rev("notes")}</label>
          <textarea rows={3} value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} style={isPendingField("notes") ? pendingStyle : undefined}/>
        </div>
        {isAdmin && (
          <div style={{gridColumn:"1 / -1", display:"flex", alignItems:"center", gap:8, margin:"4px 0", paddingTop:8, borderTop:"1px solid var(--border)"}}>
            <input type="checkbox" checked={form.is_active} onChange={e=>setForm({...form,is_active:e.target.checked})} id={`active_${company.id}`}/>
            <label htmlFor={`active_${company.id}`} style={{cursor:"pointer",fontSize:13,fontWeight:400,textTransform:"none",letterSpacing:"normal",color:"var(--text1)"}}>Active (uncheck to disable — admin only)</label>
          </div>
        )}
        {canBdx && (
          <div style={{gridColumn:"1 / -1", display:"flex", alignItems:"center", gap:8, margin:"4px 0"}}>
            <input type="checkbox" checked={bdxEligible} onChange={e=>setBdxEligible(e.target.checked)} id={`bdx_${company.id}`}/>
            <label htmlFor={`bdx_${company.id}`} style={{cursor:"pointer",fontSize:13,fontWeight:400,textTransform:"none",letterSpacing:"normal",color:"var(--text1)"}}>
              Bordereau client — claims with this company on the slip become BDX-reportable and it appears in the Reports → Bordereau picker
            </label>
          </div>
        )}
      </div>

      {company.reviewed_at && (
        <div style={{marginTop:12,fontSize:11,color:"var(--text3)"}}>
          Last reviewed: {new Date(company.reviewed_at).toLocaleString()}
        </div>
      )}

      {/* Chunk 7G: read-only linked-data sections. Click-through closes this
          modal via the parent-supplied nav callbacks. */}
      <LinkedContactsList
        companyId={company.id}
        onNavigateToContact={(id) => openLinkedContact(id)}
        refreshKey={contactsRefresh}
        actions={<button className="btn btn-outline btn-sm" onClick={() => setAddingContact(true)}>+ Add contact</button>}
      />
      {addingContact && (
        <EditContactModal
          contact={null}
          fixedCompanyId={company.id}
          fixedCompanyName={company.company_name}
          isAdmin={can("approve_contacts")}
          onClose={() => setAddingContact(false)}
          onCreated={() => { setAddingContact(false); setContactsRefresh(k => k + 1); }}
        />
      )}
      <LinkedClaimsList
        url={`/companies/${company.id}/claims`}
        rolesField="company_roles"
        onNavigateToClaim={onNavigateToClaim ? (id) => { onClose?.(); onNavigateToClaim(id); } : undefined}
      />

      {/* GO_LIVE P3 / INV-WS-1G (2026-07-03): this company's billing recipient
          profiles (AP contact / email / cc / terms / remittance) — the templates
          that prefill invoice Bill-To + email recipients. manage_invoicing only. */}
      {can("manage_invoicing") && <BillingProfilesSection companyId={company.id} />}

      {/* DC-3 (2026-06-17): company document lens — W9, payee docs, correspondence.
          Edit gated on manage_payees; view on the broad payee/directory cap server-side. */}
      <div style={{ marginTop: 18 }}>
        <EntityDocuments entityType="company" entityId={company.id} canEdit={can("manage_payees")} defaultPhi={false} />
      </div>
    </Modal>
  );
}

// ── INV-WS-1G — per-company billing recipient profiles (list / add / edit / delete).
// These prefill the invoice Bill-To snapshot and the "Email invoice" recipients.
function BillingProfilesSection({ companyId }) {
  const [profiles, setProfiles] = useState([]);
  const [editing, setEditing] = useState(null);   // null | {} (new) | profile row
  const [err, setErr] = useState(null);
  const load = () => apiFetch(`/billing-recipient-profiles?company_id=${companyId}`)
    .then(d => setProfiles(d.profiles || [])).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId]);
  const del = (p) => {
    if (!window.confirm(`Delete billing profile "${p.label}"? Invoices that already used it keep their own snapshot.`)) return;
    apiFetch(`/billing-recipient-profiles/${p.id}`, { method: "DELETE" }).then(load)
      .catch(e => setErr(parseErr(e, "Failed to delete profile")));
  };
  return (
    <div style={{ marginTop: 18 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: "var(--navy,#000066)", textTransform: "uppercase", letterSpacing: 0.4 }}>
          Billing recipient profiles ({profiles.length})</span>
        <button className="btn btn-outline btn-sm" onClick={() => setEditing({})}>+ Add profile</button>
      </div>
      {err && <div className="alert-error" style={{ marginBottom: 8 }}>{err}</div>}
      {profiles.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--text3)", padding: "6px 0" }}>
          No billing profiles yet. A profile stores this client's AP recipient (name / email / cc),
          default terms, and remittance text so invoices prefill instead of being re-keyed.</div>
      ) : (
        <table style={{ width: "100%", fontSize: 12.5 }}>
          <tbody>{profiles.map(p => (
            <tr key={p.id} style={{ borderTop: "1px solid var(--border)" }}>
              <td style={{ padding: "6px 8px", fontWeight: 600 }}>{p.label}</td>
              <td style={{ padding: "6px 8px", color: "var(--text2)" }}>{p.email || "—"}{p.cc_email ? ` · cc ${p.cc_email}` : ""}</td>
              <td style={{ padding: "6px 8px", color: "var(--text3)" }}>{p.default_terms || ""}</td>
              <td style={{ padding: "6px 8px", textAlign: "right", whiteSpace: "nowrap" }}>
                <button className="btn btn-outline btn-sm" style={{ padding: "2px 8px", fontSize: 11, marginRight: 4 }} onClick={() => setEditing(p)}>Edit</button>
                <button className="btn btn-outline btn-sm" style={{ padding: "2px 8px", fontSize: 11, color: "#991b1b" }} onClick={() => del(p)}>Delete</button>
              </td>
            </tr>))}</tbody>
        </table>
      )}
      {editing && <BillingProfileForm companyId={companyId} profile={editing}
        onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function BillingProfileForm({ companyId, profile, onClose, onSaved }) {
  const isNew = !profile.id;
  const [f, setF] = useState({
    label: profile.label || "", invoice_to: profile.invoice_to || "", attn: profile.attn || "",
    address: profile.address || "", city: profile.city || "", state: profile.state || "",
    zip: profile.zip || "", email: profile.email || "", cc_email: profile.cc_email || "",
    default_terms: profile.default_terms || "", remittance_instructions: profile.remittance_instructions || "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!f.label.trim()) { setErr("A profile label is required."); return; }
    setSaving(true); setErr(null);
    try {
      if (isNew) await apiFetch("/billing-recipient-profiles", { method: "POST", body: { ...f, company_id: companyId } });
      else await apiFetch(`/billing-recipient-profiles/${profile.id}`, { method: "PATCH", body: f });
      onSaved();
    } catch (e) { setErr(parseErr(e, "Failed to save profile")); setSaving(false); }
  };
  const lbl = { fontSize: 10.5, color: "var(--text3)", textTransform: "uppercase", display: "block", marginBottom: 2 };
  const inp = { width: "100%", boxSizing: "border-box", padding: "5px 8px", fontSize: 12.5, border: "1px solid var(--border)", borderRadius: 6 };
  const F = (label, key, ph) => (<div><label style={lbl}>{label}</label><input value={f[key]} onChange={set(key)} style={inp} placeholder={ph || ""} /></div>);
  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: 8, padding: 12, marginTop: 8, background: "var(--bg3,#f8fafc)", display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 700 }}>{isNew ? "New billing profile" : `Edit "${profile.label}"`}</div>
      {err && <div className="alert-error">{err}</div>}
      {F("Label *", "label", "e.g. AP Dept — quarterly billing")}
      <div style={{ display: "flex", gap: 8 }}>
        <div style={{ flex: 1 }}>{F("Invoice To (name on invoice)", "invoice_to")}</div>
        <div style={{ flex: 1 }}>{F("Attention", "attn")}</div>
      </div>
      {F("Address", "address")}
      <div style={{ display: "flex", gap: 8 }}>
        <div style={{ flex: 2 }}>{F("City", "city")}</div>
        <div style={{ flex: 1 }}>{F("State", "state")}</div>
        <div style={{ flex: 1 }}>{F("Zip", "zip")}</div>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <div style={{ flex: 1 }}>{F("Billing email (To)", "email")}</div>
        <div style={{ flex: 1 }}>{F("CC email", "cc_email")}</div>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <div style={{ flex: 1 }}>
          <label style={lbl}>Default terms</label>
          <select value={f.default_terms} onChange={set("default_terms")} style={inp}>
            <option value="">— none —</option>
            {["Net 15", "Net 30", "Net 45", "Net 60", "Due on receipt"].map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div style={{ flex: 2 }}>{F("Remittance instructions (overrides default)", "remittance_instructions")}</div>
      </div>
      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
        <button className="btn btn-outline btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save profile"}</button>
      </div>
    </div>
  );
}
